import type { PropsWithChildren, ReactNode } from "react";
import { ArrowClockwise, CheckCircle, MagnifyingGlass, X } from "@phosphor-icons/react";

export function StatusTag({
  value,
  tone,
}: {
  value: string;
  tone?: "green" | "red" | "amber" | "blue" | "gray";
}) {
  const inferred =
    tone ||
    (["online", "succeeded", "approved", "enabled", "active"].includes(value)
      ? "green"
      : ["failed", "critical", "disabled", "rejected"].includes(value)
        ? "red"
        : ["warning", "pending", "queued", "running"].includes(value)
          ? "amber"
          : "gray");
  const labels: Record<string, string> = {
    online: "在线",
    offline: "离线",
    installed: "已安装",
    missing: "未安装",
    enabled: "已启用",
    disabled: "已禁用",
    unbound: "未绑定",
    created: "待审批",
    queued: "排队中",
    dispatching: "派发中",
    sent: "已发送",
    acknowledged: "已确认",
    running: "执行中",
    succeeded: "成功",
    failed: "失败",
    timeout: "超时",
    cancelled: "已取消",
    pending: "待审批",
    approved: "已通过",
    rejected: "已拒绝",
    critical: "严重",
    warning: "警告",
    active: "处理中",
    idle: "空闲",
    unknown: "未知",
  };
  return <span className={`status-tag ${inferred}`}>{labels[value] || value}</span>;
}

export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange(value: string): void;
  placeholder: string;
}) {
  return (
    <label className="search-input">
      <MagnifyingGlass size={17} />
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

export function MetricStrip({
  metrics,
}: {
  metrics: Array<{
    label: string;
    value: ReactNode;
    note: string;
    tone?: string;
  }>;
}) {
  return (
    <section className="metric-strip" aria-label="模块摘要">
      {metrics.map((metric) => (
        <div key={metric.label}>
          <span>{metric.label}</span>
          <strong className={metric.tone}>{metric.value}</strong>
          <small>{metric.note}</small>
        </div>
      ))}
    </section>
  );
}

export function DataFreshness({
  updatedAt,
  isFetching,
  onRefresh,
  compact = false,
}: {
  updatedAt?: string;
  isFetching?: boolean;
  onRefresh(): void;
  compact?: boolean;
}) {
  const updatedLabel = updatedAt
    ? new Intl.DateTimeFormat("zh-CN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(new Date(updatedAt))
    : "等待首次同步";

  return (
    <div className={`data-freshness ${compact ? "compact" : ""}`} role="status">
      <CheckCircle size={14} weight="fill" aria-hidden="true" />
      <span>{isFetching ? "正在同步" : `更新于 ${updatedLabel}`}</span>
      <button
        type="button"
        aria-label="立即刷新当前数据"
        title="立即刷新"
        disabled={isFetching}
        onClick={onRefresh}
      >
        <ArrowClockwise className={isFetching ? "spin" : ""} size={14} />
      </button>
    </div>
  );
}

export function Modal({
  title,
  description,
  onClose,
  children,
  footer,
}: PropsWithChildren<{
  title: string;
  description?: string;
  onClose(): void;
  footer?: ReactNode;
}>) {
  return (
    <div className="overlay" role="presentation" onMouseDown={onClose}>
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <h2>{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <button className="icon-button" aria-label="关闭" onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </section>
    </div>
  );
}

export function Drawer({
  title,
  subtitle,
  onClose,
  wide = false,
  children,
}: PropsWithChildren<{
  title: string;
  subtitle?: string;
  onClose(): void;
  wide?: boolean;
}>) {
  return (
    <div className="drawer-layer" role="presentation" onMouseDown={onClose}>
      <aside
        className={`drawer${wide ? " wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="icon-button" aria-label="关闭" onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        <div className="drawer-body">{children}</div>
      </aside>
    </div>
  );
}
